/**
 * SceneManager - Handles the Three.js scene setup and rendering
 */
import * as THREE from 'three';

/**
 * Manages the 3D scene, camera, and environment
 */
export class SceneManager {
    constructor() {
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        
        // Store screen dimensions
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        
        // Camera settings
        this.defaultCameraPosition = new THREE.Vector3(0, 10, 20);
        this.cameraOffset = new THREE.Vector3(0, 8, 12); // Camera position relative to player
        
        // Environment
        this.environmentMesh = null;
        this.ground = null;
        this.arenaWalls = [];
        this.trees = [];
        
        // Lighting
        this.lights = {
            ambient: null,
            directional: null,
            hemisphere: null,
            spotlights: []
        };
        
        // Scene helpers for development
        this.helpers = {
            grid: null,
            axes: null
        };
        
        // Performance settings
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // Settings
        this.settings = {
            showHelpers: false, // Disable development helpers in production
            fogEnabled: true, // Enable fog for atmosphere
            shadowsEnabled: !this.isMobile, // Enable shadows (except on mobile)
            environmentSize: 500, // Size of the environment dome
            arenaRadius: 250, // Size of the arena (radius)
            wallHeight: 15, // Height of the arena walls
            treeCount: 15, // Reduced number of trees to improve performance
            wallSegments: 16, // Reduced wall segments for better performance
            enableSpotlights: !this.isMobile, // Disable spotlights on mobile
            enableAnimations: !this.isMobile // Disable animations on mobile
        };
        
        // Performance tracking
        this.lastFrameTime = 0;
        this.frameCount = 0;
        this.fps = 0;
        
        // Bind methods
        this.handleResize = this.handleResize.bind(this);
    }
    
    /**
     * Initialize the 3D scene
     * @param {THREE.WebGLRenderer} renderer - The renderer to use
     * @returns {boolean} Success
     */
    init(renderer) {
        console.log('Initializing SceneManager...');
        
        this.renderer = renderer;
        
        // Create scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x87CEEB); // Sky blue background
        
        // Add fog to scene
        if (this.settings.fogEnabled) {
            this.scene.fog = new THREE.FogExp2(0x87CEEB, 0.0015);
        }
        
        // Setup camera (perspective for 3D view)
        this.camera = new THREE.PerspectiveCamera(
            75,                                 // Field of view
            this.width / this.height,           // Aspect ratio
            0.1,                                // Near clipping plane
            1000                                // Far clipping plane
        );
        this.camera.position.copy(this.defaultCameraPosition);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
        
        // Set up lighting
        this.createLighting();
        
        // Add helpers for development (only if specifically enabled)
        if (this.settings.showHelpers) {
            this.addHelpers();
        }
        
        // Create ground
        this.createGround();
        
        // Create arena walls
        this.createArenaWalls();
        
        // Add trees to the environment
        this.addTrees();
        
        // Handle window resize
        window.addEventListener('resize', this.handleResize);
        
        // Configure renderer for shadows if enabled
        if (this.settings.shadowsEnabled && this.renderer) {
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        }
        
        console.log('SceneManager initialized');
        return true;
    }
    
    /**
     * Add development helpers to the scene
     */
    addHelpers() {
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot add helpers: scene is not initialized');
            return false;
        }
        
        // Grid helper
        const gridSize = 200;
        const gridDivisions = 200;
        this.helpers.grid = new THREE.GridHelper(gridSize, gridDivisions, 0x444444, 0x222222);
        this.scene.add(this.helpers.grid);
        
        // Axes helper
        this.helpers.axes = new THREE.AxesHelper(5);
        this.scene.add(this.helpers.axes);
        
        console.log('Scene helpers added');
        return true;
    }
    
    /**
     * Create the dome environment (skybox/dome)
     */
    createEnvironment(assetManager) {
        console.log('Creating dome environment...');
        
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot create environment: scene is not initialized');
            return false;
        }
        
        // Create a dome geometry for the sky
        const domeGeometry = new THREE.SphereGeometry(this.settings.environmentSize, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
        
        // Create dome material with gradient texture
        const domeMaterial = new THREE.MeshBasicMaterial({
            side: THREE.BackSide,
            vertexColors: true
        });
        
        // Add colors to the dome vertices for a gradient sky effect
        const colors = [];
        const skyColorTop = new THREE.Color(0x4287f5);  // Deeper blue at top
        const skyColorHorizon = new THREE.Color(0x94c5ff);  // Lighter blue at horizon
        
        const positions = domeGeometry.getAttribute('position');
        for (let i = 0; i < positions.count; i++) {
            const y = positions.getY(i);
            const ratio = Math.max(0, y / this.settings.environmentSize);
            const color = new THREE.Color().lerpColors(skyColorHorizon, skyColorTop, ratio);
            colors.push(color.r, color.g, color.b);
        }
        
        domeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        
        // Create dome mesh
        this.environmentMesh = new THREE.Mesh(domeGeometry, domeMaterial);
        this.scene.add(this.environmentMesh);
        
        console.log('Dome environment created');
        return true;
    }
    
    /**
     * Create a textured ground plane
     */
    createGround() {
        console.log('Creating textured ground plane...');
        
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot create ground: scene is not initialized');
            return false;
        }
        
        // Create a very large ground geometry
        const groundSize = this.settings.arenaRadius * 2;
        const groundGeometry = new THREE.CircleGeometry(groundSize, this.isMobile ? 32 : 64); // Lower polygon count on mobile
        
        // Create textured ground material - simpler material for mobile
        const groundMaterial = new THREE.MeshStandardMaterial({
            color: 0x5d8a48, // Grass green
            roughness: 0.9,
            metalness: 0.1
        });
        
        // Add a noise pattern to the material to simulate grass - skip on mobile for performance
        if (!this.isMobile) {
            const noiseTexture = this.createNoiseTexture(512, 512);
            groundMaterial.map = noiseTexture;
            groundMaterial.bumpMap = noiseTexture;
            groundMaterial.bumpScale = 0.1;
            groundMaterial.map.wrapS = groundMaterial.map.wrapT = THREE.RepeatWrapping;
            groundMaterial.map.repeat.set(groundSize/20, groundSize/20);
        }
        
        // Create ground mesh
        this.ground = new THREE.Mesh(groundGeometry, groundMaterial);
        this.ground.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        this.ground.position.y = -0.5; // Slightly below origin
        this.ground.receiveShadow = this.settings.shadowsEnabled;
        
        // Add ground to scene
        this.scene.add(this.ground);
        
        console.log('Textured ground created');
        return true;
    }
    
    /**
     * Create a noise texture for the ground
     */
    createNoiseTexture(width, height) {
        // Use a smaller texture on mobile for performance
        if (this.isMobile) {
            width = Math.min(width, 256);
            height = Math.min(height, 256);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        
        // Fill with a base color
        context.fillStyle = '#5d8a48';
        context.fillRect(0, 0, width, height);
        
        // Add noise
        const imageData = context.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        // Process fewer pixels for better performance - process every 2nd pixel
        const stride = this.isMobile ? 8 : 4;
        
        for (let i = 0; i < data.length; i += stride) {
            // Vary the green channel slightly for grass effect
            const noise = Math.random() * 20 - 10;
            data[i] = Math.max(0, Math.min(255, data[i] + noise * 0.7));     // R
            data[i+1] = Math.max(0, Math.min(255, data[i+1] + noise));       // G
            data[i+2] = Math.max(0, Math.min(255, data[i+2] + noise * 0.5)); // B
            
            // If processing every other pixel, copy the current pixel to the next one
            if (stride > 4) {
                data[i+4] = data[i];
                data[i+5] = data[i+1];
                data[i+6] = data[i+2];
                data[i+7] = data[i+3];
            }
        }
        
        context.putImageData(imageData, 0, 0);
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        return texture;
    }
    
    /**
     * Create arena walls to form the boundary
     */
    createArenaWalls() {
        console.log('Creating arena walls...');
        
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot create arena walls: scene is not initialized');
            return false;
        }
        
        const radius = this.settings.arenaRadius;
        const wallHeight = this.settings.wallHeight;
        const segments = this.settings.wallSegments; // Number of wall segments (more = smoother circle)
        
        // Create a wall material with stone texture
        const wallMaterial = new THREE.MeshStandardMaterial({
            color: 0x888888, // Stone gray
            roughness: 0.7,
            metalness: 0.2,
            side: THREE.DoubleSide
        });
        
        // Create segments to form a circle
        for (let i = 0; i < segments; i++) {
            const angle1 = (i / segments) * Math.PI * 2;
            const angle2 = ((i + 1) / segments) * Math.PI * 2;
            
            const x1 = Math.sin(angle1) * radius;
            const z1 = Math.cos(angle1) * radius;
            
            const x2 = Math.sin(angle2) * radius;
            const z2 = Math.cos(angle2) * radius;
            
            // Create wall geometry as a plane
            const wallGeometry = new THREE.PlaneGeometry(
                Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(z2 - z1, 2)),
                wallHeight
            );
            
            // Create wall mesh
            const wall = new THREE.Mesh(wallGeometry, wallMaterial);
            
            // Position the wall
            wall.position.set((x1 + x2) / 2, wallHeight / 2, (z1 + z2) / 2);
            
            // Rotate the wall to face the center
            wall.lookAt(new THREE.Vector3(0, wallHeight / 2, 0));
            
            // Add shadows
            wall.castShadow = this.settings.shadowsEnabled;
            wall.receiveShadow = this.settings.shadowsEnabled;
            
            // Add to scene and store reference
            this.scene.add(wall);
            this.arenaWalls.push(wall);
        }
        
        console.log('Arena walls created');
        return true;
    }
    
    /**
     * Add trees around the arena
     */
    addTrees() {
        console.log('Adding trees to environment...');
        
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot add trees: scene is not initialized');
            return false;
        }
        
        const treeCount = this.settings.treeCount;
        const arenaRadius = this.settings.arenaRadius;
        const buffer = 10; // Buffer distance from the walls
        const minRadius = arenaRadius * 0.4; // Keep some trees closer to middle
        
        for (let i = 0; i < treeCount; i++) {
            // Create a simple tree
            const tree = this.createTree();
            
            // Random position inside the arena (but not too close to the walls)
            const angle = Math.random() * Math.PI * 2;
            const radiusPos = minRadius + Math.random() * (arenaRadius - buffer - minRadius);
            const x = Math.sin(angle) * radiusPos;
            const z = Math.cos(angle) * radiusPos;
            
            // Position the tree
            tree.position.set(x, 0, z);
            
            // Rotate tree randomly
            tree.rotation.y = Math.random() * Math.PI * 2;
            
            // Scale tree randomly
            const scale = 0.7 + Math.random() * 0.6;
            tree.scale.set(scale, scale, scale);
            
            // Add to scene and store reference
            this.scene.add(tree);
            this.trees.push(tree);
        }
        
        console.log(`${treeCount} trees added to environment`);
        return true;
    }
    
    /**
     * Create a simple tree using Three.js primitives
     */
    createTree() {
        // Create a group for the tree
        const treeGroup = new THREE.Group();
        
        // Use simpler tree geometry on mobile
        const segmentsRadial = this.isMobile ? 6 : 8;
        const segmentsHeight = this.isMobile ? 1 : 1;
        
        // Create trunk
        const trunkGeometry = new THREE.CylinderGeometry(0.5, 0.7, 3, segmentsRadial);
        const trunkMaterial = new THREE.MeshStandardMaterial({
            color: 0x8B4513, // Brown
            roughness: 0.9
        });
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        trunk.position.y = 1.5; // Half the trunk height
        trunk.castShadow = this.settings.shadowsEnabled;
        trunk.receiveShadow = this.settings.shadowsEnabled;
        treeGroup.add(trunk);
        
        // Create foliage (several cones for a more interesting look)
        const foliageMaterial = new THREE.MeshStandardMaterial({
            color: 0x2E8B57, // Dark green
            roughness: 0.8
        });
        
        // Main foliage
        const foliageGeometry1 = new THREE.ConeGeometry(2, 4, segmentsRadial, segmentsHeight);
        const foliage1 = new THREE.Mesh(foliageGeometry1, foliageMaterial);
        foliage1.position.y = 4.5;
        foliage1.castShadow = this.settings.shadowsEnabled;
        treeGroup.add(foliage1);
        
        // Secondary foliage (smaller and lower)
        // Add second foliage cone only on desktop for better performance on mobile
        if (!this.isMobile) {
            const foliageGeometry2 = new THREE.ConeGeometry(2.5, 3, segmentsRadial, segmentsHeight);
            const foliage2 = new THREE.Mesh(foliageGeometry2, foliageMaterial);
            foliage2.position.y = 3;
            foliage2.castShadow = this.settings.shadowsEnabled;
            treeGroup.add(foliage2);
        }
        
        return treeGroup;
    }
    
    /**
     * Create lighting for the scene
     */
    createLighting() {
        console.log('Setting up lighting...');
        
        // Check if scene exists before proceeding
        if (!this.scene) {
            console.error('Cannot create lighting: scene is not initialized');
            return false;
        }
        
        // Ambient light for overall illumination
        this.lights.ambient = new THREE.AmbientLight(0xffffff, 0.6); // Increased to compensate for fewer lights
        this.scene.add(this.lights.ambient);
        
        // Directional light for shadows (sun-like)
        this.lights.directional = new THREE.DirectionalLight(0xffffff, 0.9); // Increased brightness
        this.lights.directional.position.set(50, 100, 50);
        this.lights.directional.castShadow = this.settings.shadowsEnabled;
        
        // Configure shadow parameters for better quality
        if (this.settings.shadowsEnabled) {
            const shadowSize = 200;
            this.lights.directional.shadow.camera.left = -shadowSize;
            this.lights.directional.shadow.camera.right = shadowSize;
            this.lights.directional.shadow.camera.top = shadowSize;
            this.lights.directional.shadow.camera.bottom = -shadowSize;
            this.lights.directional.shadow.camera.near = 1;
            this.lights.directional.shadow.camera.far = 500;
            
            // Adjust shadow quality based on mobile
            if (this.isMobile) {
                this.lights.directional.shadow.mapSize.width = 1024;
                this.lights.directional.shadow.mapSize.height = 1024;
            } else {
                this.lights.directional.shadow.mapSize.width = 2048;
                this.lights.directional.shadow.mapSize.height = 2048;
            }
        }
        
        this.scene.add(this.lights.directional);
        
        // Hemisphere light for subtle color variation
        this.lights.hemisphere = new THREE.HemisphereLight(0xB1E1FF, 0xB97A20, 0.5);
        this.scene.add(this.lights.hemisphere);
        
        // Add spotlights only if enabled (performance optimization)
        if (this.settings.enableSpotlights) {
            // Add a few spotlights around the arena for dramatic effect
            const spotlightColors = [0xffaa00, 0x00aaff, 0xaaff00];
            const spotlightPositions = [
                {x: 100, y: 50, z: -100},
                {x: -100, y: 50, z: 100},
                {x: -100, y: 50, z: -100}
            ];
            
            spotlightPositions.forEach((pos, i) => {
                const spotlight = new THREE.SpotLight(spotlightColors[i], 0.7);
                spotlight.position.set(pos.x, pos.y, pos.z);
                spotlight.target.position.set(0, 0, 0);
                spotlight.angle = Math.PI / 6;
                spotlight.penumbra = 0.3;
                spotlight.decay = 2;
                spotlight.distance = 300;
                
                // Only enable shadows on desktop and when shadows are enabled
                spotlight.castShadow = this.settings.shadowsEnabled && !this.isMobile;
                if (spotlight.castShadow) {
                    spotlight.shadow.mapSize.width = 1024;
                    spotlight.shadow.mapSize.height = 1024;
                    spotlight.shadow.camera.near = 10;
                    spotlight.shadow.camera.far = 400;
                }
                
                this.scene.add(spotlight);
                this.scene.add(spotlight.target);
                this.lights.spotlights.push(spotlight);
            });
        }
        
        console.log('Lighting setup complete');
        return true;
    }
    
    /**
     * Update camera position to follow player
     * @param {THREE.Vector3} playerPosition - The player's current position
     */
    updateCameraPosition(playerPosition) {
        if (!this.camera) return;
        
        // Calculate target camera position
        const idealOffset = new THREE.Vector3().copy(this.cameraOffset);
        
        // Adjust camera position based on player position
        this.camera.position.x = playerPosition.x + idealOffset.x;
        this.camera.position.y = playerPosition.y + idealOffset.y;
        this.camera.position.z = playerPosition.z + idealOffset.z;
        
        // Look at point slightly above player for better view
        this.camera.lookAt(
            playerPosition.x,
            playerPosition.y + 1, // Look a bit above player
            playerPosition.z
        );
    }
    
    /**
     * Handle window resize events
     */
    handleResize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        
        if (this.camera) {
            this.camera.aspect = this.width / this.height;
            this.camera.updateProjectionMatrix();
        }
        
        if (this.renderer) {
            this.renderer.setSize(this.width, this.height);
            this.renderer.setPixelRatio(window.devicePixelRatio);
        }
    }
    
    /**
     * Update rendering settings based on device type
     * @param {string} deviceType - 'mobile' or 'desktop'
     * @param {Object} settings - Settings for the device type
     */
    updateForDevice(deviceType, settings) {
        console.log(`Updating scene for ${deviceType}`);
        
        if (deviceType === 'mobile') {
            // Reduce shadow quality or disable shadows
            this.settings.shadowsEnabled = settings?.shadows || false;
            
            // Disable or simplify any performance-heavy features
            if (this.helpers.grid) this.helpers.grid.visible = false;
            if (this.helpers.axes) this.helpers.axes.visible = false;
            
            // Reduce tree detail if needed
            const reduceTreeDetail = settings?.reduceTreeDetail || true;
            if (reduceTreeDetail) {
                this.trees.forEach(tree => {
                    // Make tree less complex for mobile
                    tree.traverse(child => {
                        if (child.isMesh) {
                            child.castShadow = false;
                            child.receiveShadow = false;
                        }
                    });
                });
            }
        } else {
            // Enable high quality settings for desktop
            this.settings.shadowsEnabled = settings?.shadows || true;
            
            // Update shadow settings
            if (this.renderer) {
                this.renderer.shadowMap.enabled = this.settings.shadowsEnabled;
            }
            
            // Update tree settings
            this.trees.forEach(tree => {
                tree.traverse(child => {
                    if (child.isMesh) {
                        child.castShadow = this.settings.shadowsEnabled;
                        child.receiveShadow = this.settings.shadowsEnabled;
                    }
                });
            });
        }
        
        // Update all lights
        if (this.lights.directional) {
            this.lights.directional.castShadow = this.settings.shadowsEnabled;
        }
        
        if (this.lights.spotlights) {
            this.lights.spotlights.forEach(light => {
                light.castShadow = this.settings.shadowsEnabled;
            });
        }
    }
    
    /**
     * Update function called each frame
     * @param {number} deltaTime - Time elapsed since last frame
     */
    update(deltaTime) {
        // Track performance
        const now = performance.now();
        if (this.lastFrameTime > 0) {
            this.frameCount++;
            const elapsed = now - this.lastFrameTime;
            if (elapsed > 1000) {
                this.fps = Math.round(this.frameCount * 1000 / elapsed);
                this.frameCount = 0;
                this.lastFrameTime = now;
                
                // Log FPS every 5 seconds for debugging
                if (this.fps < 30) {
                    console.warn(`Low FPS detected: ${this.fps}`);
                }
            }
        } else {
            this.lastFrameTime = now;
        }
        
        // Skip animations if disabled for performance
        if (!this.settings.enableAnimations) {
            return;
        }
        
        // Update any animated elements
        if (this.environmentMesh) {
            // Environment rotation is subtle and doesn't need to be updated every frame
            if (Math.random() < 0.2) { // Only update in 20% of frames
                this.environmentMesh.rotation.y += deltaTime * 0.005; // Reduced rotation speed
            }
        }
        
        // Animate spotlights for dynamic lighting (less frequently)
        if (this.lights.spotlights && this.lights.spotlights.length > 0 && Math.random() < 0.3) {
            // Only update in 30% of frames to save performance
            this.lights.spotlights.forEach((light, index) => {
                const time = Date.now() * 0.0005; // Slowed down animation
                const intensity = 0.5 + Math.sin(time * 0.5 + index) * 0.15; // Reduced intensity variation
                light.intensity = intensity;
            });
        }
    }
    
    /**
     * Clean up resources when disposing
     */
    dispose() {
        console.log('Disposing SceneManager resources...');
        
        // Remove event listeners
        window.removeEventListener('resize', this.handleResize);
        
        // Dispose of geometries, materials, and textures
        if (this.ground) {
            this.ground.geometry.dispose();
            if (this.ground.material.map) this.ground.material.map.dispose();
            this.ground.material.dispose();
        }
        
        // Dispose of arena walls
        this.arenaWalls.forEach(wall => {
            wall.geometry.dispose();
            wall.material.dispose();
        });
        
        // Dispose of trees
        this.trees.forEach(tree => {
            tree.traverse(child => {
                if (child.isMesh) {
                    child.geometry.dispose();
                    child.material.dispose();
                }
            });
        });
        
        // Dispose of environment mesh
        if (this.environmentMesh) {
            this.environmentMesh.geometry.dispose();
            this.environmentMesh.material.dispose();
        }
        
        // Remove everything from the scene
        while (this.scene && this.scene.children.length > 0) {
            this.scene.remove(this.scene.children[0]);
        }
        
        console.log('SceneManager resources disposed');
    }
} 